import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Pagination from '../../components/Pagination';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, X, Save, Upload, Mail, MailPlus, AlertTriangle, ScanFace, CheckCircle2, RotateCcw, ShieldAlert, Archive, Eye, EyeOff } from 'lucide-react';
import { formatDate } from '../../lib/format';
import BulkEmployeeImportModal from '../../components/BulkEmployeeImportModal';
import UserEmailCombobox from '../../components/UserEmailCombobox';
import { useRowSelection } from '../../lib/hooks/useRowSelection';
import { useAuth } from '../../contexts/AuthContext';
import SearchableSelect from '../../components/SearchableSelect';
import EmployeeITAssets from '../it/EmployeeITAssets';
import EmployeeOnboardingWizard from '../../components/EmployeeOnboardingWizard';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { useFormFieldHelpers } from '../../hooks/useFormFieldHelpers';
import { formatINR } from '../../lib/format';

const API = process.env.REACT_APP_BACKEND_URL;

const calcWorkingYears = (doj) => {
  if (!doj) return null;
  const start = new Date(doj);
  if (Number.isNaN(start.getTime())) return null;
  const diffMs = Date.now() - start.getTime();
  const years = diffMs / (1000 * 60 * 60 * 24 * 365.25);
  if (years < 0) return null;
  const y = Math.floor(years);
  const m = Math.floor((years - y) * 12);
  if (y === 0) return `${m} mo`;
  if (m === 0) return `${y} yr${y > 1 ? 's' : ''}`;
  return `${y} yr${y > 1 ? 's' : ''} ${m} mo`;
};

const EMPTY_FORM = {
  emp_id: '', name: '', designation: '', department: '',
  project_ids: [],  // BUILD 1: multi-site access (no primary project field)
  posting_type: '', office_location_id: '',
  doj: '', dob: '', gender: '', blood_group: '',
  email: '', phone: '', emergency_contact: '', address: '',
  pan: '', aadhaar: '', pf_no: '', uan: '', esi_no: '',
  bank_name: '', bank_account: '', ifsc: '',
  basic: 0, hra: 0, allowances: 0,
  gross_salary: '', annual_ctc: '', opt_out_pf: false, opt_out_pf_cap: false,
  reporting_manager_id: '', reporting_manager_name: '',
  // Phase D · referral capture at onboarding
  referred_by_employee_id: '', referee_position: '', referee_gross_for_bonus: '',
  // Probation tracking
  probation_period_days: 90,
  notice_period_days: '',
  employment_status: 'Probation',
  status: 'Active',
  // Change 19 — employment type
  employment_type: 'On-Roll',
  consultant_payment_type: '',
  consultant_fixed_amount: null,
  consultant_payment_basis: '',
  // Explicit RBAC role (resolved from Department + Grade)
  role_id: '',
  grade: '',
  // V2 RBAC — Phase 3
  senior_profile: '',   // optional named senior-management profile
  executive_role: '',   // PM/Director title — grants elevated DPR/approval authority
};

export default function EmployeeMaster() {
  const { user: currentUser, checkAuth } = useAuth() || {};
  const canResetFace = (() => {
    const role = (currentUser?.role || '').toLowerCase();
    const dept = (currentUser?.department || '').toUpperCase();
    return role === 'super_admin' || dept === 'HR' || dept === 'MD';
  })();
  // Only super_admin / IT may edit an existing Employee ID (immutable for others).
  const canEditEmpId = (() => {
    const role = (currentUser?.role || '').toLowerCase();
    const dept = (currentUser?.department || '').toUpperCase();
    return role === 'super_admin' || role === 'it' || dept === 'IT';
  })();
  const isSuperAdmin = (currentUser?.role || '').toLowerCase() === 'super_admin';
  const [view, setView] = useState('active');     // 'active' | 'archived'
  const [archived, setArchived] = useState([]);    // archived employees (is_deleted)
  const [employees, setEmployees] = useState([]);
  const [allEmployeesFull, setAllEmployeesFull] = useState([]); // unfiltered — for Reporting Manager / Referrer pickers, never affected by the table search box
  const [empPage, setEmpPage] = useState(1);
  const [empPageSize, setEmpPageSize] = useState(50);
  const [empTotal, setEmpTotal] = useState(0);
  const [searchQ, setSearchQ] = useState('');   // debounced server-side search
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterReviewOnly, setFilterReviewOnly] = useState(false);
  const [filterProbationDue, setFilterProbationDue] = useState(false);
  const [probationDueIds, setProbationDueIds] = useState(new Set());
  // G10 (30-Sep-2026 Chunk C): salary masked by default — eye reveals, resets to hidden on reopen
  const [revealAllSalary, setRevealAllSalary] = useState(false);
  const [revealedSalaryIds, setRevealedSalaryIds] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showWizard, setShowWizard] = useState(false);  // Phase 3 onboarding wizard
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [officeLocations, setOfficeLocations] = useState([]);
  const [deptOpts, setDeptOpts] = useState([]);
  const [deptNoProfile, setDeptNoProfile] = useState([]);
  // rbacRoles kept as empty state placeholder for backward compat — no longer fetched
  // since Access Department and Executive Role fields were removed from the form.
  const [detailEmp, setDetailEmp] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const selection = useRowSelection();

  // Bulk resend a set of explicit IDs OR a preset target
  const bulkResend = async ({ ids, target, resetPw }) => {
    const body = ids ? { employee_ids: ids, reset_password: resetPw } : { target, reset_password: resetPw };
    try {
      const out = await apiPost('/hr/employees/bulk-resend-welcome', body);
      toast.success(`Sent ${out.sent_count} welcome emails${out.skipped_count ? ` · skipped ${out.skipped_count}` : ''}${resetPw ? ' (with password reset)' : ''}`);
      selection.clear();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk send failed');
    }
  };

  const selectedIds = Array.from(selection.selected || []);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      // use apiGet (Bearer token from localStorage) instead of raw fetch
      // with cookies, because the platform proxy sets `Access-Control-Allow-Origin: *`
      // which forces browsers to drop credentials cross-origin (Safari ITP / cross-domain).
      // Cookie-based auth fails silently with 401 in those cases.
      const d = await apiGet('/hr/employees', { page: empPage, limit: empPageSize, search: searchQ || undefined });
      setEmployees(Array.isArray(d?.items) ? d.items : []);
      setEmpTotal(Number(d?.total || 0));
    } finally {
      setLoading(false);
    }
  }, [empPage, empPageSize, searchQ]);

  const fetchArchived = useCallback(async () => {
    try {
      const d = await apiGet('/hr/employees?include_archived=true');
      setArchived((Array.isArray(d) ? d : []).filter((e) => e.is_deleted));
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    fetchEmployees();
    fetchArchived();
    apiGet('/hr/employees').then(d => setAllEmployeesFull(Array.isArray(d) ? d : (d?.items || []))).catch(() => {});
    apiGet('/hr/assignable-projects')
      .then(d => setProjects(Array.isArray(d) ? d : []))
      .catch(() => {});
    apiGet('/mobile/attendance/office-locations')
      .then(d => setOfficeLocations(Array.isArray(d?.items) ? d.items : []))
      .catch(() => {});
    // Read department list from the master list (curated via Settings → Master Lists)
    apiGet('/user-management/departments').then((r) => {
      if (r?.departments?.length) setDeptOpts(r.departments);
      setDeptNoProfile(r?.without_profile || []);
    }).catch(() => {});
  }, [fetchEmployees, fetchArchived]);

  // search/paging happen server-side; only the review toggle is a client-side view filter
  const filtered = employees.filter(e => !(filterReviewOnly && !e.department_needs_review) && !(filterProbationDue && !probationDueIds.has(e.id)));
  useEffect(() => { const t = setTimeout(() => { setSearchQ(search.trim()); setEmpPage(1); }, 300); return () => clearTimeout(t); }, [search]);
  const reviewCount = employees.filter(e => e.department_needs_review).length;
  useEffect(() => {
    apiGet('/hr/employees/probation-due').then(r => setProbationDueIds(new Set((r?.employees || []).map(e => e.id)))).catch(() => {});
  }, []);

  const openNew = () => { setEditingId(null); setForm(EMPTY_FORM); setFormErrors({}); setShowForm(true); };
  const openEdit = (emp) => {
    setEditingId(emp.id);
    setForm({
      ...EMPTY_FORM,
      ...emp,
      // Ensure project_ids is always a valid array even if API returns undefined
      project_ids: Array.isArray(emp.project_ids) ? emp.project_ids
        : emp.project_id ? [emp.project_id]
        : [],
      // V2: preserve senior_profile from stored record
      senior_profile: emp.senior_profile || '',
      executive_role: emp.executive_role || '',
    });
    setFormErrors({});
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); setFormErrors({}); };

  // ?edit={employee_id} — auto-open edit modal (used by Onboarding redirect)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && employees.length > 0) {
      const emp = employees.find(e => e.id === editId || e.emp_id === editId);
      if (emp) {
        openEdit(emp);
        // Clear the param so re-renders don't keep re-opening it
        setSearchParams({});
        toast.info(`Editing ${emp.name} — please complete missing fields and Save.`);
      }
    }
    // eslint-disable-next-line
  }, [employees, searchParams]);

  const submit = async () => {
    const errors = {};
    if (!form.name || !form.name.trim()) {
      errors.name = 'Employee name is required';
    }
    if (editingId && !form.emp_id) {
      errors.emp_id = 'Employee ID is missing';
    }
    if (!editingId && !(form.email || '').trim()) {
      errors.email = 'Email is required to auto-create the login account';
    }
    // Fix 1 — Date of Joining is required. The welcome and announcement
    // emails read this field; if blank, those emails show "—" for DoJ.
    if (!form.doj || !String(form.doj).trim()) {
      errors.doj = 'Date of Joining is required (appears on welcome & announcement emails)';
    }
    // Iter 289 — Reporting Manager mandatory on create (approval routing).
    // Mirrors the backend rule; top-of-chain designations are exempt.
    const EXEMPT_DESIGNATIONS = ['md', 'cmd', 'ceo', 'managing director', 'chairman & managing director'];
    if (!editingId && !(form.reporting_manager_id || '').trim()
        && !EXEMPT_DESIGNATIONS.includes((form.designation || '').trim().toLowerCase())) {
      errors.reporting_manager_id = 'Reporting Manager is required — attendance & leave approvals route to them (exempt: MD/CMD/CEO)';
    }
    // Reject negative numbers in monetary / duration fields.
    for (const [k, lbl] of [['basic', 'Basic'], ['hra', 'HRA'], ['allowances', 'Allowances'],
                            ['gross_salary', 'Gross Salary'], ['annual_ctc', 'Annual CTC'],
                            ['probation_period_days', 'Probation Period']]) {
      const v = form[k];
      if (v !== '' && v !== null && v !== undefined && Number(v) < 0) errors[k] = `${lbl} cannot be negative`;
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Scroll the modal to the top so the user sees the validation banner
      const modalEl = document.querySelector('[data-testid="emp-form-modal"]');
      if (modalEl) modalEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      toast.error(Object.values(errors)[0]);
      return;
    }
    // Fix 1 — Work Location soft warning (non-blocking).
    if (!form.work_location || !String(form.work_location).trim()) {
      if (!form.office_location_id && !form.project_ids?.length) {
        toast.warning(
          'Work Location not set — it will appear blank in the announcement email to the team.',
          { duration: 5000 },
        );
      }
    }
    setFormErrors({});
    setSaving(true);
    // Normalize numeric / optional fields — Pydantic v2 rejects empty strings
    // for Optional[float] / Optional[int] which causes a 422 (and historically
    // a white-screen crash before api.js learned to coerce 422 detail arrays).
    const toNum = (v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };
    const toInt = (v) => {
      if (v === '' || v === null || v === undefined) return null;
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : null;
    };
    const payload = {
      ...form,
      basic: parseFloat(form.basic) || 0,
      hra: parseFloat(form.hra) || 0,
      allowances: parseFloat(form.allowances) || 0,
      gross_salary: toNum(form.gross_salary),
      annual_ctc: toNum(form.annual_ctc),
      probation_period_days: toInt(form.probation_period_days),
      notice_period_days: form.notice_period_days === '' || form.notice_period_days === null || form.notice_period_days === undefined ? null : toInt(form.notice_period_days),
      referee_gross_for_bonus: toNum(form.referee_gross_for_bonus),
    };
    if (!payload.referred_by_employee_id) {
      delete payload.referred_by_employee_id;
      delete payload.referee_position;
      delete payload.referee_gross_for_bonus;
    }
    try {
      if (editingId) {
        await apiPut(`/hr/employees/${editingId}`, payload);
      } else {
        await apiPost('/hr/employees', payload);
      }
      setSaving(false);
      toast.success(editingId ? 'Employee updated' : 'Employee added');
      closeForm();
      fetchEmployees();
      apiGet('/hr/employees').then(d => setAllEmployeesFull(Array.isArray(d) ? d : (d?.items || []))).catch(() => {});
      // Refresh session so project visibility updates immediately (no logout needed)
      try { await checkAuth?.(); } catch (_) {}
      // Also refresh the header project picker immediately when project assignments changed
      const projectFieldsChanged =
        payload.project_ids !== undefined || payload.all_project_access !== undefined;
      if (projectFieldsChanged && typeof window.__refreshElloryProjects === 'function') {
        window.__refreshElloryProjects();
      }
    } catch (e) {
      setSaving(false);
      const raw = e?.response?.data?.detail;
      // Pydantic v2 returns `detail` as an array of { type, loc, msg, input, url }.
      // Rendering objects as React children crashes the page (white screen).
      // Normalize to a string before storing in formErrors / toasting.
      let msg;
      if (Array.isArray(raw)) {
        msg = raw
          .map((d) => {
            const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
            return field ? `${field}: ${d?.msg || 'invalid value'}` : (d?.msg || 'Invalid input');
          })
          .join(' · ');
      } else if (raw && typeof raw === 'object') {
        msg = raw.msg || raw.detail || JSON.stringify(raw);
      } else {
        msg = raw || 'Failed to save';
      }
      setFormErrors({ _server: msg });
      toast.error(msg);
    }
  };

  const remove = async (emp) => {
    // Direct archive (reversible) — no delete-approval workflow needed.
    if (!window.confirm(`Archive ${emp.name}${emp.emp_id ? ` (${emp.emp_id})` : ''}? They will disappear from attendance, payroll and all active lists. You can restore them anytime from the Archived tab.`)) return;
    try {
      await apiDel(`/hr/employees/${emp.id}`);
      toast.success(`${emp.name} archived — find them in the Archived tab`);
      fetchEmployees();
      fetchArchived();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to archive employee');
    }
  };

  const handleRestore = async (emp) => {
    if (!window.confirm(`Restore ${emp.name} (${emp.emp_id}) back to Active? They will reappear in all active lists and dropdowns.`)) return;
    try {
      await apiPost(`/hr/employees/${emp.id}/restore`, {});
      toast.success(`${emp.name} restored to Active`);
      fetchEmployees();
      fetchArchived();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to restore employee');
    }
  };

  const handleHardDelete = async (emp) => {
    try {
      const r = await apiDel(`/hr/employees/${emp.id}/hard-delete`);
      toast.success(`${emp.name} permanently deleted — code ${r?.emp_id_freed || emp.emp_id} freed for reuse`);
      fetchEmployees();
      fetchArchived();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to permanently delete employee');
    }
  };

  const resetFace = async (emp) => {
    if (!canResetFace) {
      toast.error('Only HR, MD or Super Admin can reset face enrolment.');
      return;
    }
    const ok = window.confirm(
      `Reset face enrolment for ${emp.name}?\n\n` +
      `This will DELETE the stored 3-pose face descriptors and force ${emp.name} to re-enrol on their mobile device ` +
      `(via /m/enroll) before they can punch attendance again.\n\n` +
      `This action is audit-logged as CRITICAL. Continue?`
    );
    if (!ok) return;
    try {
      await apiDel(`/mobile/attendance/enrollment/employee/${emp.id}`);
      toast.success(
        `Face enrolment reset for ${emp.name}. They must re-enrol (3 poses: Front · Left · Right) on the mobile app before next punch.`,
        { duration: 6000 }
      );
      fetchEmployees();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Network error while resetting face enrolment');
    }
  };

  const resendWelcome = async (emp) => {
    if (!emp.email) {
      toast.error('No email on this employee — add one first');
      return;
    }
    const resetPw = window.confirm(
      `Re-send welcome email to ${emp.name} <${emp.email}>?\n\n` +
      `Click OK to ALSO reset their password to a new one-time password (must change on next login).\n` +
      `Click Cancel to send the welcome email WITHOUT credentials.`
    );
    try {
      const out = await apiPost(`/hr/employees/${emp.id}/resend-welcome?reset_password=${resetPw}`, {});
      if (out.credentials_reset && out.temp_password) {
        // Show one-time password to HR — never stored in plaintext after this
        window.alert(`Password reset for ${emp.name}.\nOne-time password (show to employee once): ${out.temp_password}\nEmployee must change it on first login.`);
      }
      toast.success(
        `Welcome email sent to ${emp.email}` +
        (out.credentials_reset ? ' · password reset (one-time)' : '')
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Network error sending welcome email');
    }
  };

  // Confirm an employee post-probation. Flips employment_status,
  // auto-issues the Confirmation Letter, closes the open probation reminder task.
  // G9 (30-Sep-2026 Chunk C): a real dialog (effective date, or extend
  // probation instead) replaces window.confirm.
  const [confirmingIds, setConfirmingIds] = useState(() => new Set());
  const [confirmDialogEmp, setConfirmDialogEmp] = useState(null); // employee being confirmed/extended
  const [confirmMode, setConfirmMode] = useState('confirm'); // 'confirm' | 'extend'
  const [confirmEffectiveDate, setConfirmEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [extendNewEndDate, setExtendNewEndDate] = useState('');
  const [extendReason, setExtendReason] = useState('');

  const openConfirmDialog = (emp) => {
    setConfirmDialogEmp(emp);
    setConfirmMode('confirm');
    setConfirmEffectiveDate(new Date().toISOString().slice(0, 10));
    setExtendNewEndDate('');
    setExtendReason('');
  };

  const confirmEmployee = async (emp, effectiveDate) => {
    if (confirmingIds.has(emp.id)) return;  // already in flight
    setConfirmingIds((s) => new Set(s).add(emp.id));
    try {
      const res = await apiPost(`/hr/employees/${emp.id}/confirm`, { auto_letter: true, effective_date: effectiveDate });
      toast.success(
        `${emp.name} confirmed${res?.issued_letter_id ? ' · Confirmation Letter issued' : ''}`
      );
      setConfirmDialogEmp(null);
      fetchEmployees();
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      // Swallow the "already confirmed" race that happens on rapid double-tap —
      // the first call already succeeded and showed a success toast, no need to
      // confuse the user with a follow-up error.
      if (/already confirmed/i.test(detail)) { setConfirmDialogEmp(null); return; }
      toast.error(detail || 'Confirmation failed');
    } finally {
      setConfirmingIds((s) => {
        const n = new Set(s);
        n.delete(emp.id);
        return n;
      });
    }
  };

  const extendProbation = async (emp, newEndDate, reason) => {
    if (!newEndDate) { toast.error('New probation end date is required'); return; }
    setConfirmingIds((s) => new Set(s).add(emp.id));
    try {
      await apiPost(`/hr/employees/${emp.id}/extend-probation`, { new_end_date: newEndDate, reason });
      toast.success(`${emp.name}'s probation extended to ${newEndDate}`);
      setConfirmDialogEmp(null);
      fetchEmployees();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to extend probation');
    } finally {
      setConfirmingIds((s) => {
        const n = new Set(s);
        n.delete(emp.id);
        return n;
      });
    }
  };

  return (
    <div className="space-y-4" data-testid="employee-master">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="relative w-full sm:w-80">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            data-testid="emp-search"
            placeholder="Search by name, ID, designation…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border border-slate-200 rounded-md pl-9 pr-3 py-2 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowBulkImport(true)}
            data-testid="bulk-import-open-btn"
          >
            <Upload size={14} className="mr-1.5" /> Bulk Import
          </Button>
          <Button onClick={() => setShowWizard(true)} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="add-employee-btn">
            <Plus size={14} className="mr-1.5" /> Add Employee
          </Button>
        </div>
      </div>

      <BulkEmployeeImportModal
        open={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onSuccess={() => fetchEmployees()}
      />

      {/* Active / Archived view toggle */}
      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="emp-view-tabs">
        <button
          onClick={() => setView('active')}
          data-testid="emp-tab-active"
          className={`px-4 py-2 text-sm font-display font-bold border-b-2 -mb-px transition-colors ${view === 'active' ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Active ({empTotal})
        </button>
        <button
          onClick={() => setView('archived')}
          data-testid="emp-tab-archived"
          className={`px-4 py-2 text-sm font-display font-bold border-b-2 -mb-px transition-colors ${view === 'archived' ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Archived ({archived.length})
        </button>
      </div>

      {view === 'active' && (<>

      {/* Bulk action toolbar */}
      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-sm px-4 py-2.5" data-testid="bulk-toolbar">
        <div className="flex items-center gap-3 text-xs">
          {selection.count > 0 ? (
            <>
              <span className="font-display font-bold text-[#3b4872]">{selection.count} selected</span>
              <button
                onClick={() => bulkResend({ ids: selectedIds, resetPw: false })}
                data-testid="bulk-resend-selected"
                className="flex items-center gap-1 px-3 py-1.5 border border-slate-200 rounded-sm hover:bg-slate-50"
              >
                <Mail size={12} /> Resend welcome to {selection.count}
              </button>
              <button
                onClick={() => {
                  if (!window.confirm(`Reset password to default for ${selection.count} employees and re-send credentials?`)) return;
                  bulkResend({ ids: selectedIds, resetPw: true });
                }}
                data-testid="bulk-resend-selected-reset"
                className="flex items-center gap-1 px-3 py-1.5 border border-amber-200 text-amber-800 bg-amber-50 rounded-sm hover:bg-amber-100"
              >
                <MailPlus size={12} /> Resend + Reset Password
              </button>
              <button onClick={selection.clear} className="text-slate-500 hover:text-slate-800 text-[11px] ml-2">Clear</button>
            </>
          ) : (
            <span className="text-slate-400">Select rows to enable bulk actions, or use the preset →</span>
          )}
        </div>
        <button
          onClick={() => {
            if (!window.confirm('Send a reminder email to ALL employees with incomplete onboarding (missing phone or emergency contact)?')) return;
            bulkResend({ target: 'incomplete_onboarding', resetPw: false });
          }}
          data-testid="bulk-resend-incomplete"
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#3b4872] hover:bg-[#2d3858] text-white rounded-sm font-display font-semibold"
        >
          <AlertTriangle size={12} /> Remind users with incomplete onboarding
        </button>
      </div>

      {reviewCount > 0 && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-sm text-sm" data-testid="dept-review-banner">
          <AlertTriangle size={14} className="text-amber-600 shrink-0" />
          <span><strong className="font-display">{reviewCount} employee{reviewCount > 1 ? 's' : ''}</strong> ha{reviewCount > 1 ? 've' : 's'} a department that doesn't match the current master list.</span>
          {!filterReviewOnly ? (
            <button onClick={() => setFilterReviewOnly(true)} className="ml-auto text-xs font-display font-semibold text-amber-800 underline hover:text-amber-900" data-testid="dept-review-filter-btn">Show only these →</button>
          ) : (
            <button onClick={() => setFilterReviewOnly(false)} className="ml-auto text-xs font-display font-semibold text-slate-600 underline hover:text-slate-800" data-testid="dept-review-clear-btn">Show all employees</button>
          )}
        </div>
      )}

      {probationDueIds.size > 0 && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-rose-50 border border-rose-200 rounded-sm text-sm" data-testid="probation-due-banner">
          <AlertTriangle size={14} className="text-rose-600 shrink-0" />
          <span><strong className="font-display">{probationDueIds.size} employee{probationDueIds.size > 1 ? 's' : ''}</strong> {probationDueIds.size > 1 ? 'are' : 'is'} due or overdue for confirmation.</span>
          {!filterProbationDue ? (
            <button onClick={() => setFilterProbationDue(true)} className="ml-auto text-xs font-display font-semibold text-rose-800 underline hover:text-rose-900" data-testid="probation-due-filter-btn">Show only these →</button>
          ) : (
            <button onClick={() => setFilterProbationDue(false)} className="ml-auto text-xs font-display font-semibold text-slate-600 underline hover:text-slate-800" data-testid="probation-due-clear-btn">Show all employees</button>
          )}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
            <table className="w-full text-sm sticky-col-header">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="text-center px-2 py-2.5 w-8">
                    <input
                      type="checkbox"
                      data-testid="emp-select-all"
                      checked={filtered.length > 0 && filtered.every(e => selection.isSelected(e.id))}
                      onChange={() => selection.toggleAll(filtered.map(e => e.id))}
                    />
                  </th>
                  <th className="text-left px-3 py-2.5">Emp ID</th>
                  <th className="text-left px-3 py-2.5">Name</th>
                  <th className="text-left px-3 py-2.5">Designation</th>
                  <th className="text-left px-3 py-2.5">Dept</th>
                  <th className="text-left px-3 py-2.5">DOJ</th>
                  <th className="text-right px-3 py-2.5">
                    <span className="inline-flex items-center gap-1 justify-end w-full">
                      Gross
                      <button
                        onClick={(e) => { e.stopPropagation(); setRevealAllSalary(v => !v); setRevealedSalaryIds(new Set()); }}
                        className="text-slate-400 hover:text-[#3b4872]" title={revealAllSalary ? 'Hide all salaries' : 'Reveal all salaries'}
                        data-testid="emp-list-salary-header-eye"
                      >
                        {revealAllSalary ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                    </span>
                  </th>
                  <th className="text-center px-3 py-2.5">Status</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="text-center py-8 text-slate-500">Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-8 text-slate-500">No employees found.</td></tr>
                ) : filtered.map(emp => (
                  <tr key={emp.id} className="border-b border-slate-100 hover:bg-indigo-50/30 cursor-pointer" data-testid={`emp-row-${emp.emp_id}`}>
                    <td className="px-2 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        data-testid={`emp-select-${emp.emp_id}`}
                        checked={selection.isSelected(emp.id)}
                        onChange={() => selection.toggle(emp.id)}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs" onClick={() => setDetailEmp(emp)}>{emp.emp_id}</td>
                    <td className="px-3 py-2 font-medium" onClick={() => setDetailEmp(emp)}>{emp.name}</td>
                    <td className="px-3 py-2" onClick={() => setDetailEmp(emp)}>{emp.designation || '—'}</td>
                    <td className="px-3 py-2" onClick={() => setDetailEmp(emp)}>
                      {emp.department || '—'}
                      {emp.department_needs_review && (
                        <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 font-bold" data-testid={`dept-review-badge-${emp.emp_id}`}>⚠ Review</span>
                      )}
                    </td>
                    <td className="px-3 py-2" onClick={() => setDetailEmp(emp)}>{emp.doj || '—'}</td>
                    <td className="px-3 py-2 text-right font-medium" onClick={(e) => e.stopPropagation()}>
                      <span className="inline-flex items-center gap-1.5 justify-end">
                        {(revealAllSalary || revealedSalaryIds.has(emp.id)) ? (
                          <span data-testid={`emp-gross-${emp.emp_id}`} onClick={() => setDetailEmp(emp)} className="cursor-pointer">{formatINR(emp.gross || emp.salary)}</span>
                        ) : (
                          <span className="tracking-widest text-slate-400 cursor-pointer" onClick={() => setDetailEmp(emp)} data-testid={`emp-gross-masked-${emp.emp_id}`}>₹ ••••••</span>
                        )}
                        {!revealAllSalary && (
                          <button
                            onClick={() => setRevealedSalaryIds(s => { const n = new Set(s); n.has(emp.id) ? n.delete(emp.id) : n.add(emp.id); return n; })}
                            className="text-slate-400 hover:text-[#3b4872]" data-testid={`emp-gross-eye-${emp.emp_id}`}
                          >
                            {revealedSalaryIds.has(emp.id) ? <EyeOff size={12} /> : <Eye size={12} />}
                          </button>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center" onClick={() => setDetailEmp(emp)}>
                      <span className={`text-[11px] px-2 py-0.5 rounded-sm ${
                        emp.status === 'Active' ? 'bg-emerald-50 text-emerald-700' :
                        emp.status === 'Resigned' ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-600'
                      }`}>{emp.status || 'Active'}</span>
                      {(emp.employment_status || 'Probation') !== 'Confirmed' && emp.doj && (() => {
                        const pdays = parseInt(emp.probation_period_days ?? (emp.probation_months ? emp.probation_months * 30 : 90), 10) || 90;
                        const endMs = new Date(emp.doj).getTime() + pdays * 86400000;
                        const daysLeft = Math.ceil((endMs - Date.now()) / 86400000);
                        const overdue = daysLeft < 0;
                        const due = daysLeft <= 7;
                        return (
                          <div className={`mt-1 text-[10px] font-display font-bold uppercase tracking-widest ${overdue ? 'text-rose-700' : due ? 'text-amber-700' : 'text-slate-500'}`} title={`Probation ends ${formatDate(endMs)}`}>
                            {overdue ? `Probation overdue ${Math.abs(daysLeft)}d` : daysLeft === 0 ? 'Probation ends today' : `On probation · ${daysLeft}d left`}
                          </div>
                        );
                      })()}
                      {emp.employment_status === 'Confirmed' && (
                        <div className="mt-1 text-[10px] font-display font-bold uppercase tracking-widest text-emerald-700">Confirmed</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {(emp.employment_status || 'Probation') !== 'Confirmed' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openConfirmDialog(emp); }}
                          disabled={confirmingIds.has(emp.id)}
                          className="text-emerald-700 hover:text-emerald-900 mr-2 disabled:opacity-40 disabled:cursor-wait"
                          title="Confirm employee post-probation — flips status & auto-generates Confirmation Letter"
                          data-testid={`confirm-emp-${emp.emp_id}`}
                        >
                          <CheckCircle2 size={14} />
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); resendWelcome(emp); }} className="text-slate-500 hover:text-[#3b4872] mr-2" title="Re-send welcome email" data-testid={`resend-${emp.emp_id}`}>
                        <Mail size={14} />
                      </button>
                      {canResetFace && (
                        <button
                          onClick={(e) => { e.stopPropagation(); resetFace(emp); }}
                          className="text-slate-500 hover:text-amber-600 mr-2"
                          title="Reset Face Enrolment (employee must re-capture 3 poses)"
                          data-testid={`reset-face-${emp.emp_id}`}
                        >
                          <ScanFace size={14} />
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); openEdit(emp); }} className="text-slate-500 hover:text-[#3b4872] mr-2" data-testid={`edit-${emp.emp_id}`}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); remove(emp); }} className="text-slate-500 hover:text-red-600" title="Archive employee (reversible)" data-testid={`del-${emp.emp_id}`}>
                        <Archive size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-200 px-4 py-3">
            <Pagination page={empPage} pageSize={empPageSize} total={empTotal}
              start={(empPage - 1) * empPageSize} end={Math.min(empPage * empPageSize, empTotal)}
              onPageChange={setEmpPage} onPageSizeChange={(n) => { setEmpPageSize(n); setEmpPage(1); }}
              label="employees" data-testid="employee-pagination" />
          </div>
        </CardContent>
      </Card>
      </>)}

      {view === 'archived' && (
        <ArchivedEmployees
          rows={archived}
          isSuperAdmin={isSuperAdmin}
          canManage={canResetFace}
          onRestore={handleRestore}
          onHardDelete={handleHardDelete}
        />
      )}

      {showForm && (
        <EmployeeFormModal
          form={form}
          setForm={setForm}
          onClose={closeForm}
          onSubmit={submit}
          editingId={editingId}
          projects={projects}
          officeLocations={officeLocations}
          allEmployees={allEmployeesFull}
          deptOpts={deptOpts}
          errors={formErrors}
          saving={saving}
          canEditEmpId={canEditEmpId}
        />
      )}

      {showWizard && (
        <EmployeeOnboardingWizard
          onClose={() => setShowWizard(false)}
          onCreated={(emp) => {
            setShowWizard(false);
            fetchEmployees();
            apiGet('/hr/employees').then(d => setAllEmployeesFull(Array.isArray(d) ? d : (d?.items || []))).catch(() => {});
            toast.success(`${emp.employment_type === 'Consultant' ? 'Consultant' : 'Employee'} ${emp.name} created successfully`);
          }}
          projects={projects}
          officeLocations={officeLocations}
          allEmployees={allEmployeesFull}
          deptOpts={deptOpts}
          deptNoProfile={deptNoProfile}
        />
      )}

      {detailEmp && (
        <EmployeeDetailModal
          emp={detailEmp}
          onClose={() => setDetailEmp(null)}
          onEdit={() => { openEdit(detailEmp); setDetailEmp(null); }}
          onResetFace={canResetFace ? () => { resetFace(detailEmp); setDetailEmp(null); } : null}
        />
      )}

      {confirmDialogEmp && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setConfirmDialogEmp(null)} data-testid="probation-confirm-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-md">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-display font-bold text-lg">Probation — {confirmDialogEmp.name}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{confirmDialogEmp.emp_id}</p>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex gap-2">
                <button onClick={() => setConfirmMode('confirm')} data-testid="probation-mode-confirm-btn"
                  className={`flex-1 px-3 py-2 text-xs font-display font-semibold rounded-sm border ${confirmMode === 'confirm' ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-300 text-slate-600'}`}>
                  Confirm Employee
                </button>
                <button onClick={() => setConfirmMode('extend')} data-testid="probation-mode-extend-btn"
                  className={`flex-1 px-3 py-2 text-xs font-display font-semibold rounded-sm border ${confirmMode === 'extend' ? 'bg-amber-600 text-white border-amber-600' : 'border-slate-300 text-slate-600'}`}>
                  Extend Probation
                </button>
              </div>
              {confirmMode === 'confirm' ? (
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Effective Date</label>
                  <input type="date" value={confirmEffectiveDate} onChange={(e) => setConfirmEffectiveDate(e.target.value)} data-testid="probation-confirm-effective-date" className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm" />
                  <p className="text-[10px] text-slate-500 mt-1.5">
                    This will mark {confirmDialogEmp.name} as Confirmed (no longer on probation), auto-generate a Confirmation Letter dated {confirmEffectiveDate}, and allow them to apply for leaves.
                  </p>
                </div>
              ) : (
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">New Probation End Date</label>
                  <input type="date" value={extendNewEndDate} onChange={(e) => setExtendNewEndDate(e.target.value)} data-testid="probation-extend-new-date" className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm" />
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5 mt-3">Reason</label>
                  <textarea value={extendReason} onChange={(e) => setExtendReason(e.target.value)} rows={2} data-testid="probation-extend-reason" className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm" placeholder="Why is probation being extended?" />
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setConfirmDialogEmp(null)} className="px-4 py-2 text-sm border border-slate-300 rounded font-display" data-testid="probation-dialog-cancel-btn">Cancel</button>
              {confirmMode === 'confirm' ? (
                <button onClick={() => confirmEmployee(confirmDialogEmp, confirmEffectiveDate)} disabled={confirmingIds.has(confirmDialogEmp.id)}
                  className="px-5 py-2 text-sm bg-emerald-600 text-white rounded font-display font-semibold disabled:opacity-50" data-testid="probation-dialog-confirm-btn">
                  {confirmingIds.has(confirmDialogEmp.id) ? 'Confirming…' : 'Confirm'}
                </button>
              ) : (
                <button onClick={() => extendProbation(confirmDialogEmp, extendNewEndDate, extendReason)} disabled={confirmingIds.has(confirmDialogEmp.id) || !extendNewEndDate}
                  className="px-5 py-2 text-sm bg-amber-600 text-white rounded font-display font-semibold disabled:opacity-50" data-testid="probation-dialog-extend-btn">
                  {confirmingIds.has(confirmDialogEmp.id) ? 'Saving…' : 'Extend Probation'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmployeeFormModal({ form, setForm, onClose, onSubmit, editingId, projects, officeLocations = [], allEmployees = [], deptOpts = [], deptNoProfile = [], errors = {}, saving = false, canEditEmpId = false }) {
  const update = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), []);
  const ff = useFormFieldHelpers('employee_master');
  const gross = (parseFloat(form.basic) || 0) + (parseFloat(form.hra) || 0) + (parseFloat(form.allowances) || 0);
  const hasErrors = Object.keys(errors).length > 0;
  // G10 (30-Sep-2026 Chunk C): salary section masked until revealed — resets every time this modal (re)opens
  const [salaryRevealed, setSalaryRevealed] = useState(false);
  // Round 3 #1: PF mandatory signal from SalaryBreakdownPreview — READ-ONLY, never modifies opt_out_pf
  const [pfMandatory, setPfMandatory] = React.useState(false);
  const [pfCeiling, setPfCeiling] = React.useState(25000);
  // Manager options = every other employee (can't be your own manager).
  // The dropdown itself is searchable via the browser's built-in select
  // type-ahead; no separate search input is needed.
  const mgrOptions = React.useMemo(() => {
    return allEmployees
      .filter((e) => e.id !== editingId)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [allEmployees, editingId]);

  // --- RBAC: Grade is a stored attribute only (no role resolution) ----------
  // Department drives RBAC via db.department_profiles. Grade is HR data only.
  const ACCESS_GRADES = ['M1', 'M2', 'M3', 'S1', 'S2', 'W1'];
  const isSeniorLeadership = form.department === 'Senior Leadership';

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-md w-full max-w-3xl my-6" onClick={e => e.stopPropagation()} data-testid="emp-form-modal">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <div className="text-[10px] font-display uppercase tracking-[0.18em] text-slate-500">{editingId ? 'Edit' : 'New'} Employee</div>
            <h3 className="font-display font-bold text-lg">{form.name || 'Employee Profile'}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          {hasErrors && (
            <div className="bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2 rounded-md flex items-start gap-2" data-testid="emp-form-errors">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <ul className="space-y-0.5">
                {Object.entries(errors).map(([k, v]) => (
                  <li key={k}>{typeof v === 'string' ? v : (v?.msg || JSON.stringify(v))}</li>
                ))}
              </ul>
            </div>
          )}
          <FormSection title="Identity">
            <Field label={editingId ? `${ff.labelOf('emp_id','Employee ID')}${ff.reqStar('emp_id', true)}` : 'Employee ID (leave blank to auto-generate)'}>
              <input
                value={form.emp_id}
                onChange={(e) => update('emp_id', e.target.value)}
                disabled={!!editingId && !canEditEmpId}
                required={!!editingId && ff.isReq('emp_id', true)}
                placeholder={editingId ? '' : 'Auto from Number Series (EEPC000123)'}
                className={inp}
                data-testid="emp-form-emp-id"
              />
              {editingId && canEditEmpId && (
                <p className="text-[11px] text-amber-600 mt-1">Editing the Employee ID re-points all historical records to the new code.</p>
              )}
            </Field>
            <Field label={`${ff.labelOf('name','Full Name')}${ff.reqStar('name', true)}`}>
              <input value={form.name} onChange={(e) => update('name', e.target.value)} required={ff.isReq('name', true)} className={`${inp} ${errors.name ? 'border-red-400 ring-1 ring-red-300' : ''}`} data-testid="emp-form-name" />
            </Field>
            {ff.showField('designation') && (
            <Field label={`${ff.labelOf('designation','Designation')}${ff.reqStar('designation', true)}`}>
              <input
                value={form.designation}
                onChange={(e) => update('designation', e.target.value)}
                required={ff.isReq('designation', true)}
                className={inp}
                placeholder="e.g. Site Engineer, Project Manager"
                data-testid="emp-form-designation"
              />
            </Field>
            )}
            <Field label="Executive Role" hint="Grants elevated project-wide DPR fill/finalise authority — leave blank for normal dept-scoped staff">
              <select
                value={form.executive_role}
                onChange={(e) => update('executive_role', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
                data-testid="emp-form-executive-role"
              >
                <option value="">Not applicable</option>
                <optgroup label="Project Manager">
                  <option value="Project Manager">Project Manager</option>
                  <option value="Sr. Project Manager">Sr. Project Manager</option>
                  <option value="Asst. Project Manager">Asst. Project Manager</option>
                  <option value="Dy. Project Manager">Dy. Project Manager</option>
                </optgroup>
                <optgroup label="Project Director">
                  <option value="Project Director">Project Director</option>
                  <option value="Sr. Project Director">Sr. Project Director</option>
                  <option value="Dy. Project Director">Dy. Project Director</option>
                </optgroup>
                {/* Stored value doesn't match a known option (e.g. a legacy/free-text
                    title) — show it instead of silently falling back to "Not
                    applicable", which misled HR into thinking the field was empty. */}
                {form.executive_role && ![
                  'Project Manager', 'Sr. Project Manager', 'Asst. Project Manager', 'Dy. Project Manager',
                  'Project Director', 'Sr. Project Director', 'Dy. Project Director',
                ].includes(form.executive_role) && (
                  <option value={form.executive_role}>{form.executive_role} (current)</option>
                )}
              </select>
            </Field>
            {ff.showField('department') && (
            <Field label={`${ff.labelOf('department','Department')}${ff.reqStar('department', true)}`}>
              <select
                value={form.department}
                onChange={(e) => {
                  const dept = e.target.value;
                  update('department', dept);
                  if (dept !== 'Senior Leadership') update('senior_profile', '');
                }}
                required={ff.isReq('department', true)}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
                data-testid="emp-form-department"
              >
                <option value="">Select department…</option>
                {deptOpts.map(d => (
                  <option key={d} value={d} disabled={deptNoProfile.includes(d)}>
                    {d}{deptNoProfile.includes(d) ? ' (no permission profile)' : ''}
                  </option>
                ))}
              </select>
              {deptNoProfile.includes(form.department) && (
                <p className="text-[11px] text-amber-600 mt-1" data-testid="emp-form-department-no-profile-warning">
                  ⚠ "{form.department}" has no matching permission profile — saving will fail. Pick a different department or ask an admin to create one under Settings → Roles &amp; Access.
                </p>
              )}
            </Field>
            )}
            {/* PM setup guide — shown when Project Management is selected */}
            {form.department === 'Project Management' && (
              <div className="col-span-2 p-3 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-900" data-testid="pm-setup-guide">
                <p className="font-display font-bold mb-1.5">How to give this employee full Project Manager access:</p>
                <ol className="list-decimal ml-4 space-y-1 text-blue-800">
                  <li>Department = <strong>"Project Management"</strong> drives PM permissions via the Department Profile in Roles &amp; Access Control.</li>
                  <li>Tick the <strong>projects</strong> this PM is responsible for in the "Projects" section — they will see only those projects' data.</li>
                  <li>Save the employee, then ask the PM to <strong>log out and log back in</strong> for the new permissions to take effect.</li>
                  <li>To verify: PM opens Approvals Hub — should see their projects' MPRs under the PROCUREMENT tab.</li>
                </ol>
              </div>
            )}
            <Field label="Grade" hint="HR grade for payroll banding. Stored only — does not drive system access.">
              <select
                value={form.grade || ''}
                onChange={(e) => update('grade', e.target.value)}
                className={inp}
                data-testid="emp-form-grade"
              >
                <option value="">— Select grade —</option>
                {ACCESS_GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
            {/* Senior Management Profile — only for Senior Leadership employees */}
            {isSeniorLeadership && (
            <Field label="Senior Management Profile (V2)" hint="Grants full executive permissions for this leadership role.">
              <SeniorProfileSelect value={form.senior_profile || ''} onChange={v => update('senior_profile', v)} />
              {form.senior_profile && (
                <p className="text-[10px] text-emerald-600 mt-1 leading-snug" data-testid="emp-form-resolved-role">
                  Senior profile applied: <strong>{form.senior_profile}</strong> — executive permissions active.
                </p>
              )}
            </Field>
            )}
            <Field label="Employment Type">
              <select
                value={form.employment_type || 'On-Roll'}
                onChange={(e) => update('employment_type', e.target.value)}
                className={inp}
                data-testid="emp-form-employment-type"
              >
                <option value="On-Roll">On-Roll</option>
                <option value="Consultant">Consultant</option>
              </select>
              <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                <strong>Consultant</strong> = no probation, no onboarding checklist, no payroll/payslip.
                Attendance, leave, ESS and project access work the same as On-Roll.
              </p>
            </Field>
            {form.employment_type === 'Consultant' && (
              <>
                <Field label="Payment Type">
                  <select
                    value={form.consultant_payment_type || ''}
                    onChange={(e) => update('consultant_payment_type', e.target.value)}
                    className={inp}
                    data-testid="emp-form-consultant-payment-type"
                  >
                    <option value="">— Select —</option>
                    <option value="Fixed">Fixed monthly retainer</option>
                    <option value="Performance">Performance-based</option>
                  </select>
                </Field>
                {form.consultant_payment_type === 'Fixed' && (
                  <Field label="Fixed Amount (₹/month)">
                    <input
                      type="number"
                      min="0"
                      value={form.consultant_fixed_amount ?? ''}
                      onChange={(e) => update('consultant_fixed_amount', e.target.value === '' ? null : Number(e.target.value))}
                      placeholder="e.g. 50000"
                      className={inp}
                      data-testid="emp-form-consultant-fixed-amount"
                    />
                  </Field>
                )}
                {form.consultant_payment_type === 'Performance' && (
                  <Field label="Payment Basis">
                    <input
                      value={form.consultant_payment_basis || ''}
                      onChange={(e) => update('consultant_payment_basis', e.target.value)}
                      placeholder="e.g. per milestone, % of contract value"
                      className={inp}
                      data-testid="emp-form-consultant-payment-basis"
                    />
                  </Field>
                )}
              </>
            )}
            <Field label="Posting Type">
              <select
                value={form.posting_type || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  update('posting_type', v);
                  // Office staff link to an office location; Site staff don't.
                  if (v === 'Office') update('office_location_id', form.office_location_id || null);
                  if (v === 'Site') update('office_location_id', null);
                  // Do NOT touch all_project_access or project selection here —
                  // project assignment is now independent of posting type.
                }}
                className={inp}
                data-testid="emp-form-posting-type"
              >
                <option value="">— Select posting type —</option>
                <option value="Office">Office (ESIC-covered)</option>
                <option value="Site">Site (not ESIC-covered)</option>
              </select>
              <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                Determines ESIC eligibility: <strong>Office</strong> → ESIC (Basic up to ₹21k).
                <strong> Site</strong> → not ESIC; covered by Workmen's Compensation.
              </p>
            </Field>
            {form.posting_type === 'Office' && (
              <Field label="Office Location">
                <select
                  value={form.office_location_id || ''}
                  onChange={(e) => update('office_location_id', e.target.value || null)}
                  className={inp}
                  data-testid="emp-form-office-location"
                >
                  <option value="">— Select office —</option>
                  {officeLocations.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}{o.address ? ` · ${o.address}` : ''}</option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-500 mt-1">Registered head/branch office from Settings → Office Locations.</p>
              </Field>
            )}
            {/* BUILD 1: multi-site access picker — always shown, posting type independent */}
            <Field label="Projects (assigned sites)" full>
              <p className="text-[10px] text-slate-500 mb-1.5">
                Select all projects this employee works on. They will only see data from these projects across the system.
                Leave the all-access toggle below ON only for genuine head-office / multi-project coordinators.
              </p>
              <div className="space-y-1 max-h-44 overflow-y-auto border border-slate-200 rounded-sm p-2 bg-white" data-testid="emp-project-ids-picker">
                {projects.map(p => (
                  <label key={p.id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.project_ids.includes(p.id)}
                      onChange={e => update('project_ids',
                        e.target.checked
                          ? [...form.project_ids, p.id]
                          : form.project_ids.filter(x => x !== p.id)
                      )}
                      data-testid={`emp-project-ids-${p.id}`}
                    />
                    <span>{p.name}</span>
                  </label>
                ))}
                {projects.length === 0 && <p className="text-xs text-slate-400 italic">No active projects available</p>}
              </div>
            </Field>
            {/* All-project access toggle (for multi-project coordinators) */}
            <Field label="Project Access" full>
              <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="emp-all-project-access">
                <div
                  onClick={() => update('all_project_access', !form.all_project_access)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    form.all_project_access ? 'bg-[#3b4872]' : 'bg-slate-200'
                  }`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                    form.all_project_access ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </div>
                <div>
                  <p className="text-sm font-medium">All-project access (sees every project)</p>
                  <p className="text-[10px] text-slate-500">
                    Turn ON only for head-office staff or coordinators who genuinely need every project.
                    Leave OFF so the employee sees only the projects selected above.
                  </p>
                </div>
              </label>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => update('status', e.target.value)} className={inp}>
                {['Active', 'On Leave', 'Resigned', 'Terminated'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label={editingId ? 'Reporting Manager' : 'Reporting Manager *'} full>
              <div className="space-y-1.5">
                <SearchableSelect
                  value={form.reporting_manager_id || ''}
                  onChange={(id, mgr) => {
                    update('reporting_manager_id', id || null);
                    update('reporting_manager_name', mgr?.name || null);
                  }}
                  options={mgrOptions}
                  getKey={(m) => m.id}
                  getLabel={(m) => `${m.name}${m.emp_id ? ' · ' + m.emp_id : ''}${m.department ? ' · ' + m.department : ''}${m.designation ? ' · ' + m.designation : ''}`}
                  getSearchTokens={(m) => [m.name, m.emp_id, m.department, m.designation]}
                  placeholder="Search by name, employee ID, designation…"
                  emptyValueLabel="— No reporting manager —"
                  data-testid="manager-search-select"
                />
                <p className="text-[10px] text-slate-500">
                  This person can approve attendance regularization &amp; leave requests for this employee.
                </p>
              </div>
            </Field>
          </FormSection>

          <FormSection title="Personal">
            <Field label="DOB"><input type="date" value={form.dob || ''} onChange={(e) => update('dob', e.target.value)} className={inp} /></Field>
            {ff.showField('doj') && (
            <Field label={`${ff.labelOf('doj','Date of Joining')}${ff.reqStar('doj', true)}`}>
              <input type="date" value={form.doj || ''}
                     onChange={(e) => update('doj', e.target.value)}
                     required={ff.isReq('doj', true)}
                     className={`${inp} ${errors.doj ? 'border-red-400 ring-1 ring-red-300' : ''}`}
                     data-testid="emp-form-doj" />
              {errors.doj && <p className="text-xs text-red-600 mt-1" data-testid="emp-form-doj-error">{errors.doj}</p>}
            </Field>
            )}
            {form.employment_type !== 'Consultant' && (
            <Field label="Probation Period (days)">
              <input
                type="number"
                min="0"
                max="365"
                value={form.probation_period_days ?? 90}
                onChange={(e) => update('probation_period_days', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                className={inp}
                data-testid="emp-form-probation-days"
                placeholder="90"
              />
              {form.doj && form.probation_period_days >= 0 && (
                <div className="text-[10px] text-slate-500 mt-1">
                  Confirmation due:{' '}
                  <strong>
                    {formatDate(new Date(form.doj).getTime() + (parseInt(form.probation_period_days, 10) || 0) * 86400000)}
                  </strong>
                  {' · '}HR will get a reminder task 7 days before. Until confirmed, this employee cannot apply for leave.
                </div>
              )}
            </Field>
            )}
            {form.employment_type !== 'Consultant' && (
            <Field label="Notice Period">
              <select
                value={[30, 45, 60, 90].includes(parseInt(form.notice_period_days, 10)) ? form.notice_period_days : (form.notice_period_days ? 'custom' : '')}
                onChange={(e) => {
                  if (e.target.value === 'custom') { update('notice_period_days', form.notice_period_days && ![30,45,60,90].includes(parseInt(form.notice_period_days,10)) ? form.notice_period_days : ''); }
                  else update('notice_period_days', e.target.value === '' ? '' : parseInt(e.target.value, 10));
                }}
                className={inp}
                data-testid="emp-form-notice-period"
              >
                <option value="">Not set</option>
                <option value={30}>30 days</option>
                <option value={45}>45 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
                <option value="custom">Custom…</option>
              </select>
              {(form.notice_period_days && ![30, 45, 60, 90].includes(parseInt(form.notice_period_days, 10))) && (
                <input
                  type="number" min="0" max="365"
                  value={form.notice_period_days ?? ''}
                  onChange={(e) => update('notice_period_days', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                  className={`${inp} mt-2`} placeholder="Custom days"
                  data-testid="emp-form-notice-period-custom"
                />
              )}
              <p className="text-[10px] text-slate-500 mt-1">Used on offer/appointment letters and defaults the exit form's notice period. Blank shows "Not set".</p>
            </Field>
            )}
            <Field label="Gender">
              <select value={form.gender || ''} onChange={(e) => update('gender', e.target.value)} className={inp}>
                <option value="">—</option>
                {['Male', 'Female', 'Other'].map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
            <Field label="Blood Group">
              <select value={form.blood_group || ''} onChange={(e) => update('blood_group', e.target.value)} className={inp}>
                <option value="">—</option>
                {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
            <UserEmailCombobox
              value={form.email}
              onChange={(email, matched) => {
                update('email', email);
                // If user picked an existing user, also pre-fill name/department if blank
                if (matched && !form.name) update('name', matched.name);
                if (matched && !form.department && matched.department) update('department', matched.department);
              }}
              editingId={editingId}
              testid="emp-form-email"
            />
            {ff.showField('phone') && (
            <Field label={`${ff.labelOf('phone','Phone')}${ff.reqStar('phone', false)}`}>
              <input value={form.phone || ''} onChange={(e) => update('phone', e.target.value)} required={ff.isReq('phone', false)} className={inp} />
            </Field>
            )}
            <Field label="Emergency Contact"><input value={form.emergency_contact || ''} onChange={(e) => update('emergency_contact', e.target.value)} className={inp} /></Field>
            <Field label="Address" full><input value={form.address || ''} onChange={(e) => update('address', e.target.value)} className={inp} /></Field>
          </FormSection>

          <FormSection title="Statutory IDs">
            {ff.showField('pan') && (
            <Field label={`${ff.labelOf('pan','PAN')}${ff.reqStar('pan', false)}`}>
              <input value={form.pan || ''} onChange={(e) => update('pan', e.target.value.toUpperCase())} required={ff.isReq('pan', false)} className={inp} />
            </Field>
            )}
            {ff.showField('aadhar') && (
            <Field label={`${ff.labelOf('aadhar','Aadhaar')}${ff.reqStar('aadhar', false)}`}>
              <input value={form.aadhaar || ''} onChange={(e) => update('aadhaar', e.target.value)} required={ff.isReq('aadhar', false)} className={inp} />
            </Field>
            )}
            {ff.showField('uan') && (
            <Field label={`${ff.labelOf('uan','UAN')}${ff.reqStar('uan', false)}`}>
              <input value={form.uan || ''} onChange={(e) => update('uan', e.target.value)} required={ff.isReq('uan', false)} className={inp} />
            </Field>
            )}
            <Field label="PF No"><input value={form.pf_no || ''} onChange={(e) => update('pf_no', e.target.value)} className={inp} /></Field>
            <Field label="ESI No"><input value={form.esi_no || ''} onChange={(e) => update('esi_no', e.target.value)} className={inp} /></Field>
          </FormSection>

          <FormSection title="Bank">
            <Field label="Bank Name"><input value={form.bank_name || ''} onChange={(e) => update('bank_name', e.target.value)} className={inp} /></Field>
            {ff.showField('bank_account') && (
            <Field label={`${ff.labelOf('bank_account','Account No')}${ff.reqStar('bank_account', false)}`}>
              <input value={form.bank_account || ''} onChange={(e) => update('bank_account', e.target.value)} required={ff.isReq('bank_account', false)} className={inp} />
            </Field>
            )}
            {ff.showField('ifsc') && (
            <Field label={`${ff.labelOf('ifsc','IFSC')}${ff.reqStar('ifsc', false)}`}>
              <input value={form.ifsc || ''} onChange={(e) => update('ifsc', e.target.value.toUpperCase())} required={ff.isReq('ifsc', false)} className={inp} />
            </Field>
            )}
          </FormSection>

          <FormSection title={
            <span className="inline-flex items-center gap-2">
              Salary Structure (Monthly)
              <button type="button" onClick={() => setSalaryRevealed(v => !v)} className="text-slate-400 hover:text-[#3b4872]" data-testid="emp-form-salary-eye" title={salaryRevealed ? 'Hide salary' : 'Reveal to edit'}>
                {salaryRevealed ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            </span>
          }>
            {!salaryRevealed ? (
              <div className="col-span-2 sm:col-span-full flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-sm px-4 py-6" data-testid="emp-form-salary-masked">
                <span className="tracking-widest text-slate-400 text-lg">₹ ••••••••</span>
                <button type="button" onClick={() => setSalaryRevealed(true)} className="text-xs font-display font-bold uppercase tracking-wider text-[#3b4872] flex items-center gap-1">
                  <Eye size={13} /> Reveal to edit
                </button>
              </div>
            ) : (
            <>
            <Field label="Gross Salary (₹) *" full>
              <input type="number" min="0" value={form.gross_salary || ''} onChange={(e) => update('gross_salary', e.target.value)} className={inp} placeholder="Enter gross to auto-compute breakdown" data-testid="emp-gross-salary" />
              <p className="text-[10px] text-slate-500 mt-1">All components below are derived from this gross using the Salary Structure Master.</p>
            </Field>
            <Field label="Annual CTC (₹)" full>
              <input type="number" min="0" value={form.annual_ctc || ''} onChange={(e) => update('annual_ctc', e.target.value)} className={inp} placeholder="Auto-computed from bracket policy — override if needed" data-testid="emp-annual-ctc" />
              <p className="text-[10px] text-slate-500 mt-1">Auto-derived from Gross × 12 (+ Employer PF for ≤₹25k brackets, + EDLI + Admin). Gratuity, Employer ESI and TDS are excluded. You can override manually.</p>
            </Field>
            <SalaryBreakdownPreview
              gross={form.gross_salary}
              optOutPf={form.opt_out_pf}
              optOutPfCap={form.opt_out_pf_cap}
              employeeId={form.id}
              gender={form.gender}
              postingType={form.posting_type}
              onCtcChange={update}
              onPfMandatory={(mandatory, ceiling) => {
                // Round 3 #1: READ-ONLY signal — never change opt_out_pf here.
                // HR must manually update opted-out employees listed in Data Checks.
                setPfMandatory(mandatory);
                if (ceiling) setPfCeiling(ceiling);
              }}
            />
            </>
            )}
            {salaryRevealed && (
            <Field label="PF Contribution Mode" full>
              <div className="space-y-1.5" data-testid="emp-pf-option">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" name="pf_option" checked={!form.opt_out_pf && !form.opt_out_pf_cap}
                    onChange={() => { update('opt_out_pf', false); update('opt_out_pf_cap', false); }} className="mt-1" data-testid="emp-pf-capped" />
                  <span className="text-sm">
                    <b>Capped at ₹{Math.round(pfCeiling * 0.12).toLocaleString('en-IN')}/mo</b>{' '}
                    <span className="text-slate-500">(default · 12% of min(Basic+DA, ₹{pfCeiling.toLocaleString('en-IN')}))</span>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" name="pf_option" checked={!!form.opt_out_pf_cap && !form.opt_out_pf}
                    onChange={() => { update('opt_out_pf', false); update('opt_out_pf_cap', true); }} className="mt-1" data-testid="emp-pf-full" />
                  <span className="text-sm">
                    <b>Full PF (no cap)</b> <span className="text-slate-500">— 12% on entire Basic + DA. Employee gets a higher take-home PF; employer matches.</span>
                  </span>
                </label>
                <label className={`flex items-start gap-2 ${pfMandatory && !form.opt_out_pf ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="radio"
                    name="pf_option"
                    checked={!!form.opt_out_pf}
                    disabled={pfMandatory && !form.opt_out_pf}
                    onChange={() => { if (!pfMandatory || form.opt_out_pf) { update('opt_out_pf', true); update('opt_out_pf_cap', false); } }}
                    className="mt-1"
                    data-testid="emp-pf-optout"
                  />
                  <span className="text-sm">
                    <b>Opted out (Form 11)</b> <span className="text-slate-500">— no PF deduction either side. Only legal if employee declared opt-out at joining and was never enrolled before.</span>
                  </span>
                </label>
              </div>
              {/* Round 3 #1: Two clear cases — never silently flip opt_out_pf */}
              {pfMandatory && !form.opt_out_pf && (
                <p className="text-[10px] text-amber-700 mt-1.5 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="pf-mandatory-note">
                  PF is mandatory when Basic + DA is ₹{pfCeiling.toLocaleString('en-IN')} or less. "Opted out (Form 11)" is disabled for this employee.
                </p>
              )}
              {pfMandatory && form.opt_out_pf && (
                <p className="text-[10px] text-amber-700 mt-1.5 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="pf-mandatory-warning">
                  Opted out, but PF is mandatory at this salary (Basic+DA ≤ ₹{pfCeiling.toLocaleString('en-IN')}). Listed in HR Data Checks. HR must correct this manually.
                </p>
              )}
              {!pfMandatory && (
                <p className="text-[10px] text-slate-500 mt-1.5">Default is the statutory cap. PF is mandatory when Basic + DA is ₹{pfCeiling.toLocaleString('en-IN')} or less. Opt-out is only legal under Form 11 conditions.</p>
              )}
            </Field>
            )}
          </FormSection>

          <FormSection title="Referral (optional)">
            <Field label="Referred by Employee" full>
              <select
                value={form.referred_by_employee_id || ''}
                onChange={(e) => update('referred_by_employee_id', e.target.value || '')}
                className={inp}
                data-testid="referrer-select"
              >
                <option value="">— Not a referral hire —</option>
                {allEmployees
                  .filter((e) => {
                    const d = (e.department || '').toUpperCase();
                    return !d.includes('HR') && !d.includes('ADMIN');
                  })
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name} {e.emp_id ? `· ${e.emp_id}` : ''} {e.department ? `· ${e.department}` : ''}
                    </option>
                  ))}
              </select>
              <p className="text-[10px] text-slate-500 mt-1">
                Per the Employee Referral Bonus Policy, HR &amp; Admin staff are not eligible to refer.
                Selecting a referrer auto-creates a referral record; the 50% bonus is computed from this employee's gross above and paid after their 6-month probation.
              </p>
            </Field>
            <Field label="Referee Position (override)">
              <input
                type="text"
                value={form.referee_position || ''}
                onChange={(e) => update('referee_position', e.target.value)}
                className={inp}
                placeholder="Defaults to designation"
              />
            </Field>
            <Field label="Override Gross for Bonus Calc (₹)">
              <input
                type="number"
                value={form.referee_gross_for_bonus || ''}
                onChange={(e) => update('referee_gross_for_bonus', e.target.value)}
                className={inp}
                placeholder={`Defaults to ₹${gross.toLocaleString('en-IN')}`}
              />
            </Field>
          </FormSection>
        </div>

        <div className="border-t px-5 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={onSubmit} disabled={saving} className="bg-[#3b4872] hover:bg-[#2c3658]" data-testid="emp-save-btn">
            <Save size={14} className="mr-1.5" /> {saving ? 'Saving…' : (editingId ? 'Update' : 'Create')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EmployeeDetailModal({ emp, onClose, onEdit, onResetFace }) {
  // G10 (30-Sep-2026 Chunk C): salary section starts masked every time this modal opens
  const [salaryRevealed, setSalaryRevealed] = useState(false);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-md w-full max-w-3xl my-6" onClick={e => e.stopPropagation()} data-testid="emp-detail-modal">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <div className="text-[10px] font-display uppercase tracking-[0.18em] text-slate-500">{emp.emp_id}</div>
            <h3 className="font-display font-bold text-xl">{emp.name}</h3>
            <div className="text-sm text-slate-500">{emp.designation} · {emp.department}{emp.work_location ? ` · ${emp.work_location}` : ''}</div>
          </div>
          <div className="flex gap-2">
            {onResetFace && (
              <Button
                variant="outline"
                size="sm"
                onClick={onResetFace}
                className="border-amber-200 text-amber-800 hover:bg-amber-50"
                data-testid="emp-detail-reset-face"
                title="Reset face enrolment (employee must re-capture 3 poses)"
              >
                <ScanFace size={13} className="mr-1" />Re-enrol Face
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onEdit}><Pencil size={13} className="mr-1" />Edit</Button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
        </div>
        <div className="p-5 space-y-5">
          <DetailSection title="Employment">
            <DetailRow label="Date of Joining" value={emp.doj || emp.date_of_joining} />
            <DetailRow label="Date of Confirmation" value={emp.doc || emp.date_of_confirmation} />
            <DetailRow label="Working Years" value={calcWorkingYears(emp.doj || emp.date_of_joining)} />
            <DetailRow label="Work Location" value={emp.work_location} />
            <DetailRow label="Reporting Manager" value={emp.reporting_manager_name} accent />
          </DetailSection>

          <DetailSection title="Personal">
            <DetailRow label="DOB" value={emp.dob} />
            <DetailRow label="Gender" value={emp.gender} />
            <DetailRow label="Blood Group" value={emp.blood_group} />
            <DetailRow label="Email" value={emp.email} />
            <DetailRow label="Phone" value={emp.phone} />
            <DetailRow label="Emergency" value={emp.emergency_contact} />
            <DetailRow label="Address" value={emp.address} full />
          </DetailSection>

          <DetailSection title="Statutory IDs">
            <DetailRow label="PAN" value={emp.pan} />
            <DetailRow label="Aadhaar" value={emp.aadhaar} />
            <DetailRow label="UAN" value={emp.uan} />
            <DetailRow label="PF No" value={emp.pf_no} />
            <DetailRow label="ESI No" value={emp.esi_no} />
          </DetailSection>

          <DetailSection title="Bank Details">
            <DetailRow label="Bank Name" value={emp.bank_name} />
            <DetailRow label="Account No" value={emp.bank_account || emp.bank_account_no} />
            <DetailRow label="IFSC" value={emp.ifsc || emp.bank_ifsc} />
          </DetailSection>

          {/* A-HR §3: Career section — education + work history */}
          <CareerSection employeeId={emp.id} />

          <DetailSection title={
            <span className="inline-flex items-center gap-2">
              Salary Structure (Monthly)
              <button onClick={() => setSalaryRevealed(v => !v)} className="text-slate-400 hover:text-[#3b4872]" data-testid="emp-detail-salary-eye" title={salaryRevealed ? 'Hide salary' : 'Reveal salary'}>
                {salaryRevealed ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            </span>
          }>
            <DetailRow label="Basic" value={salaryRevealed ? (emp.basic ? formatINR(emp.basic) : null) : '₹ ••••••'} />
            <DetailRow label="HRA" value={salaryRevealed ? (emp.hra ? formatINR(emp.hra) : null) : '₹ ••••••'} />
            <DetailRow label="Conveyance" value={salaryRevealed ? (emp.conveyance ? formatINR(emp.conveyance) : null) : '₹ ••••••'} />
            <DetailRow label="Special Allowance" value={salaryRevealed ? (emp.special_allowance ? formatINR(emp.special_allowance) : null) : '₹ ••••••'} />
            <DetailRow label="Other Allowances" value={salaryRevealed ? (emp.allowances ? formatINR(emp.allowances) : null) : '₹ ••••••'} />
            <DetailRow label="Gross (monthly)" value={salaryRevealed ? (emp.gross_salary ? formatINR(emp.gross_salary) : (emp.gross ? formatINR(emp.gross) : (emp.salary ? formatINR(emp.salary) : null))) : '₹ ••••••'} accent />
            <DetailRow label="Annual CTC" value={salaryRevealed ? (emp.annual_ctc ? formatINR(emp.annual_ctc) : null) : '₹ ••••••'} accent />
          </DetailSection>

          <div className="border border-slate-200 rounded-sm p-4" data-testid="emp-detail-it-assets">
            <EmployeeITAssets empId={emp.id} />
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-display uppercase tracking-[0.18em] text-slate-500 mb-2 border-b border-slate-100 pb-1">{title}</div>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">{children}</div>
    </div>
  );
}

const inp = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#3b4872]/40";

function FormSection({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-display uppercase tracking-[0.18em] text-slate-500 mb-2">{title}</div>
      <div className="grid sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Field({ label, children, full, hint }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="text-[11px] text-slate-600 mb-1 block">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}


function DetailRow({ label, value, full, accent }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className={`text-sm ${accent ? 'font-bold text-[#3b4872]' : 'text-slate-800'}`}>{value || '—'}</div>
    </div>
  );
}

function SalaryBreakdownPreview({ gross, optOutPf, optOutPfCap, employeeId, gender, postingType, onCtcChange, onPfMandatory }) {
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [pfRules, setPfRules] = React.useState(null);  // effective PF rules for previewMonth
  // Round 2 #3: month picker so HR can see any future/historical month's breakdown
  const [previewMonth, setPreviewMonth] = React.useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  // Round 3 #1: fetch effective PF rules to get ceiling dynamically
  React.useEffect(() => {
    if (!previewMonth) return;
    apiGet(`/hr/salary-master/pf-rules/effective?month=${previewMonth}`)
      .then(r => setPfRules(r))
      .catch(() => {});
  }, [previewMonth]);

  const pfCeiling = pfRules?.pf_wage_ceiling || 25000;

  React.useEffect(() => {
    const g = parseFloat(gross) || 0;
    if (g <= 0) { setPreview(null); onPfMandatory && onPfMandatory(false, pfCeiling); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await apiPost('/hr/salary-master/preview', {
          gross: g,
          opt_out_pf: !!optOutPf,
          opt_out_pf_cap: !!optOutPfCap,
          employee_id: employeeId || undefined,
          gender: gender || undefined,
          posting_type: postingType || undefined,
          month: previewMonth,
        });
        setPreview(data);
        if (onCtcChange && data?.ctc_annual) onCtcChange('annual_ctc', Math.round(data.ctc_annual));
        // Round 3 #1: notify parent with mandatory flag AND ceiling — parent MUST NOT auto-flip opt_out_pf
        if (onPfMandatory) {
          const pfWages = data?.pf_wages_full || data?.deductions?.pf_wages_full || 0;
          onPfMandatory(pfWages > 0 && pfWages <= pfCeiling, pfCeiling);
        }
      } catch { /* swallow */ }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [gross, optOutPf, optOutPfCap, employeeId, gender, postingType, previewMonth, pfCeiling, onCtcChange, onPfMandatory]);

  if (!preview) return null;
  const mode = preview.pf_inclusion_mode || preview.deductions?.pf_inclusion_mode;
  const ded = preview.deductions || {};
  return (
    <div className="col-span-full bg-indigo-50/40 border border-indigo-200 rounded-sm p-3" data-testid="salary-breakdown-preview">
      <div className="text-[11px] font-bold uppercase tracking-wider text-[#3b4872] mb-2 flex items-center justify-between">
        <span>
          Auto-computed Breakdown · {preview.structure_version_no ? `v${preview.structure_version_no}` : ''}
          {previewMonth && <span className="font-normal text-slate-500 ml-1">for {previewMonth}</span>}
          {loading && <span className="text-slate-400 ml-1">(updating…)</span>}
        </span>
        <input
          type="month"
          value={previewMonth}
          onChange={e => setPreviewMonth(e.target.value)}
          className="text-[11px] border border-indigo-200 rounded px-1.5 py-0.5 bg-white ml-2"
          title="Show breakdown for this month"
          data-testid="salary-preview-month-picker"
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs">
        {preview.components.map(c => (
          <div key={c.code} className="flex justify-between">
            <span className="text-slate-600">{c.name || c.code}</span>
            <span className="font-mono">₹{Math.round(c.amount).toLocaleString('en-IN')}</span>
          </div>
        ))}
      </div>
      <div className="border-t border-indigo-200 mt-2 pt-2 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs">
        {/* Round 2 #3: No employer-side lines. Only employee deductions. */}
        {mode !== 'both_above' && (
          <div className="flex justify-between">
            <span className="text-red-600">PF (employee)</span>
            <span className="font-mono">₹{Math.round(ded.employee_pf || 0).toLocaleString('en-IN')}</span>
          </div>
        )}
        {mode === 'both_within' && (
          <div className="flex justify-between">
            <span className="text-red-600">PF – Employer share (CTC-inclusive)</span>
            <span className="font-mono">₹{Math.round(ded.employer_pf || 0).toLocaleString('en-IN')}</span>
          </div>
        )}
        {/* Coverage type from compute_breakdown */}
        <div className="flex justify-between">
          <span className={`${preview.coverage_type === 'ESIC' ? 'text-red-600' : 'text-slate-400'}`}>
            {preview.coverage_type === 'ESIC' ? 'ESI (employee)' : `Coverage: ${preview.coverage_type || '—'}`}
          </span>
          {preview.coverage_type === 'ESIC' && (
            <span className="font-mono" title={`ESI base: ${ded.esi_wage_label || '—'}`}>
              ₹{Math.round(ded.employee_esi || 0).toLocaleString('en-IN')}
            </span>
          )}
        </div>
        <div className="flex justify-between">
          <span className="text-red-600">Prof Tax</span>
          <span className="font-mono">₹{Math.round(ded.professional_tax || 0).toLocaleString('en-IN')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-emerald-700 font-bold">Net pay</span>
          <span className="font-mono font-bold">₹{Math.round(preview.net).toLocaleString('en-IN')}</span>
        </div>
        <div className="col-span-full text-[10px] text-slate-500 leading-snug">
          {mode === 'both_above' && <>Gross ≤ ₹{pfCeiling > 15000 ? Math.round(pfRules?.inclusion_both_above_below || 15000).toLocaleString('en-IN') : '15,000'} — both PFs are employer-paid above gross; take-home has no PF deduction.</>}
          {mode === 'employee_within' && <>Gross ₹{(Math.round(pfRules?.inclusion_both_above_below || 15000) + 1).toLocaleString('en-IN')}–₹{Math.round(pfRules?.inclusion_employee_within_upto || pfCeiling).toLocaleString('en-IN')} — Employee PF is netted from gross. Employer PF is on top.</>}
          {mode === 'both_within' && <>Gross &gt; ₹{Math.round(pfRules?.inclusion_employee_within_upto || pfCeiling).toLocaleString('en-IN')} — both PFs are netted from gross (CTC-inclusive).</>}
        </div>
        {preview.ctc_monthly && (
          <div className="col-span-full mt-1 flex justify-between text-[11px] border-t border-indigo-200 pt-2">
            <span className="text-[#3b4872] font-bold uppercase tracking-wider">CTC · Monthly / Annual</span>
            <span className="font-mono font-bold text-[#3b4872]">
              ₹{Math.round(preview.ctc_monthly).toLocaleString('en-IN')} / ₹{Math.round(preview.ctc_annual).toLocaleString('en-IN')}
            </span>
          </div>
        )}
        {(preview.other_deductions || []).length > 0 && (
          <div className="col-span-full mt-1 border-t border-amber-300 pt-2 space-y-1" data-testid="salary-preview-advances">
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Post-statutory deductions (active salary advances)</div>
            {preview.other_deductions.map(o => (
              <div key={o.advance_id} className="flex justify-between text-[11px]">
                <span className="text-amber-800">{o.label} — EMI × {o.emis_remaining} remaining</span>
                <span className="font-mono text-amber-800">−₹{Math.round(o.amount).toLocaleString('en-IN')}</span>
              </div>
            ))}
            <div className="flex justify-between text-[11px] font-bold">
              <span className="text-emerald-700">Net after advance EMI</span>
              <span className="font-mono">₹{Math.round(preview.net_after_other_deductions).toLocaleString('en-IN')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ── Archived employees tab ──────────────────────────────────────────────────
function ArchivedEmployees({ rows, isSuperAdmin, canManage, onRestore, onHardDelete }) {
  const [confirmEmp, setConfirmEmp] = useState(null);
  const [typed, setTyped] = useState('');
  const reasonLabel = (r, emp) => {
    if (r === 'exit_completed') return 'Exit / F&F';
    if (r === 'status_exited') return emp?.exit_status ? `Marked ${emp.exit_status}` : 'Exited';
    return 'Manual';
  };
  const fmtDate = (d) => (d ? formatDate(d) : '—');

  return (
    <div className="space-y-4" data-testid="archived-view">
      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 text-sm text-amber-900" data-testid="archived-banner">
        <Archive size={16} className="mt-0.5 shrink-0" />
        <span>Archived employees are hidden from all dropdowns and active lists. Restore to re-activate, or permanently delete to free their employee code for reuse.</span>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
            <table className="w-full text-sm sticky-col-header">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-display uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2.5">Emp ID</th>
                  <th className="text-left px-3 py-2.5">Name</th>
                  <th className="text-left px-3 py-2.5">Designation</th>
                  <th className="text-left px-3 py-2.5">Dept</th>
                  <th className="text-left px-3 py-2.5">Archived On</th>
                  <th className="text-left px-3 py-2.5">Archived By</th>
                  <th className="text-left px-3 py-2.5">Reason</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-10 text-slate-500" data-testid="archived-empty">No archived employees. Deleted or exited employees appear here.</td></tr>
                ) : rows.map((emp) => (
                  <tr key={emp.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`archived-row-${emp.emp_id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{emp.emp_id}</td>
                    <td className="px-3 py-2 font-medium">{emp.name}</td>
                    <td className="px-3 py-2 text-slate-600">{emp.designation || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{emp.department || '—'}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtDate(emp.deleted_at)}</td>
                    <td className="px-3 py-2 text-slate-600">{emp.deleted_by || '—'}</td>
                    <td className="px-3 py-2"><span className="inline-block px-2 py-0.5 bg-slate-100 text-slate-600 text-[11px] rounded-sm">{reasonLabel(emp.archived_reason, emp)}</span></td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {canManage && (
                        <button onClick={() => onRestore(emp)} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 border border-emerald-200 text-emerald-700 rounded-sm hover:bg-emerald-50 mr-2" data-testid={`restore-${emp.emp_id}`}>
                          <RotateCcw size={12} /> Restore
                        </button>
                      )}
                      {isSuperAdmin && (
                        <button onClick={() => { setConfirmEmp(emp); setTyped(''); }} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 border border-rose-200 text-rose-700 rounded-sm hover:bg-rose-50" data-testid={`harddelete-${emp.emp_id}`}>
                          <Trash2 size={12} /> Permanently Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {confirmEmp && (
        <div className="fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setConfirmEmp(null); }} data-testid="harddelete-backdrop">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            <header className="px-5 py-3 bg-rose-50 border-b border-rose-200 flex items-center gap-2">
              <ShieldAlert size={16} className="text-rose-600" />
              <h2 className="font-display font-bold text-sm uppercase tracking-wide">Permanently delete employee</h2>
            </header>
            <div className="px-5 py-4 text-sm text-slate-700">
              <p>This will <strong>permanently delete {confirmEmp.name} ({confirmEmp.emp_id})</strong> and free the employee code for reuse. All historical records (attendance, payroll, leave, login) will be removed. <strong className="text-rose-700">This cannot be undone.</strong></p>
              <label className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-500 mt-4 block">
                Type <span className="font-mono text-rose-700">{confirmEmp.emp_id}</span> to confirm
              </label>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="w-full mt-1 border border-slate-300 rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:border-rose-500"
                placeholder={confirmEmp.emp_id}
                data-testid="harddelete-confirm-input"
                autoFocus
              />
            </div>
            <footer className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
              <button onClick={() => setConfirmEmp(null)} className="px-4 py-2 text-xs font-display font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-200 rounded-sm" data-testid="harddelete-cancel">Cancel</button>
              <button
                onClick={() => { const e = confirmEmp; setConfirmEmp(null); onHardDelete(e); }}
                disabled={typed.trim() !== confirmEmp.emp_id}
                className="px-4 py-2 text-xs font-display font-bold uppercase tracking-wider text-white bg-rose-600 hover:bg-rose-700 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="harddelete-confirm-btn"
              >
                Permanently Delete
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Senior Profile Select component (lazy-loads from V2 API) ────────────────
function SeniorProfileSelect({ value, onChange }) {
  const [profiles, setProfiles] = React.useState([]);
  React.useEffect(() => {
    apiGet('/rbac/v2/senior-profiles').then(r => setProfiles(r.profiles || [])).catch(() => {});
  }, []);
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:border-[#3b4872]"
      data-testid="emp-form-senior-profile">
      <option value="">— None —</option>
      {profiles.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
    </select>
  );
}


// A-HR §3: Career section — education + work history + total experience
function CareerSection({ employeeId }) {
  const [career, setCareer] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [edData, setEdData] = React.useState({ education: [], experience: [] });
  const [saving, setSaving] = React.useState(false);
  const { apiGet, apiPut } = require('../../lib/api');

  React.useEffect(() => {
    if (!employeeId) return;
    apiGet(`/hr/employees/${employeeId}/career`)
      .then(d => { setCareer(d); setEdData({ education: d.education || [], experience: d.experience || [] }); })
      .catch(() => {});
  }, [employeeId]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const updated = await apiPut(`/hr/employees/${employeeId}/career`, edData);
      setCareer(updated);
      setEditing(false);
      toast.success('Career history saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const addEdu = () => setEdData(d => ({ ...d, education: [...d.education, { id: Date.now().toString(), degree: '', institution: '', year: '', percentage: '' }] }));
  const addExp = () => setEdData(d => ({ ...d, experience: [...d.experience, { id: Date.now().toString(), company: '', role: '', from: '', to: '' }] }));
  const updEdu = (i, k, v) => setEdData(d => { const a = [...d.education]; a[i] = { ...a[i], [k]: v }; return { ...d, education: a }; });
  const updExp = (i, k, v) => setEdData(d => { const a = [...d.experience]; a[i] = { ...a[i], [k]: v }; return { ...d, experience: a }; });
  const delEdu = (i) => setEdData(d => ({ ...d, education: d.education.filter((_, j) => j !== i) }));
  const delExp = (i) => setEdData(d => ({ ...d, experience: d.experience.filter((_, j) => j !== i) }));

  if (!career) return null;

  return (
    <div className="border border-slate-200 rounded-sm p-4 space-y-3" data-testid="career-section">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm text-slate-700">Career History</h3>
        <div className="flex gap-2 items-center text-xs text-slate-500">
          <span>Total exp: <strong>{career.total_experience_years}y</strong></span>
          <span>Ellora: <strong>{career.ellora_tenure_years}y</strong></span>
          <span>Prior: <strong>{career.prior_experience_years}y</strong></span>
          <button onClick={() => setEditing(!editing)} className="px-2 py-0.5 border border-slate-300 rounded-sm hover:border-[#3b4872] text-slate-600" data-testid="career-edit-btn">
            {editing ? 'Cancel' : 'Edit'}
          </button>
        </div>
      </div>
      {!editing ? (
        <div className="space-y-2">
          {career.education?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Education</div>
              {career.education.map((e, i) => (
                <div key={i} className="text-xs text-slate-700">{e.degree} — {e.institution}{e.year ? `, ${e.year}` : ''}{e.percentage ? ` (${e.percentage}%)` : ''}</div>
              ))}
            </div>
          )}
          {career.experience?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Work History</div>
              {career.experience.map((e, i) => (
                <div key={i} className="text-xs text-slate-700">{e.role} at {e.company}{e.from ? `, ${e.from}` : ''}{e.to ? ` – ${e.to}` : ' – present'}</div>
              ))}
            </div>
          )}
          {!career.education?.length && !career.experience?.length && (
            <p className="text-xs text-slate-400">No career history on file. Click Edit to add.</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-slate-600">Education</span>
              <button onClick={addEdu} className="text-xs text-[#3b4872] underline">+ Add</button>
            </div>
            {edData.education.map((e, i) => (
              <div key={e.id} className="grid grid-cols-4 gap-2 mb-2">
                <input value={e.degree} onChange={ev=>updEdu(i,'degree',ev.target.value)} placeholder="Degree" className="border rounded px-2 py-1 text-xs col-span-1" />
                <input value={e.institution} onChange={ev=>updEdu(i,'institution',ev.target.value)} placeholder="Institution" className="border rounded px-2 py-1 text-xs col-span-1" />
                <input value={e.year||''} onChange={ev=>updEdu(i,'year',ev.target.value)} placeholder="Year" type="number" className="border rounded px-2 py-1 text-xs" />
                <div className="flex gap-1">
                  <input value={e.percentage||''} onChange={ev=>updEdu(i,'percentage',ev.target.value)} placeholder="%" className="border rounded px-2 py-1 text-xs w-16" />
                  <button onClick={()=>delEdu(i)} className="text-rose-500 text-xs px-1">✕</button>
                </div>
              </div>
            ))}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-slate-600">Work History</span>
              <button onClick={addExp} className="text-xs text-[#3b4872] underline">+ Add</button>
            </div>
            {edData.experience.map((e, i) => (
              <div key={e.id} className="grid grid-cols-5 gap-2 mb-2">
                <input value={e.company} onChange={ev=>updExp(i,'company',ev.target.value)} placeholder="Company" className="border rounded px-2 py-1 text-xs col-span-2" />
                <input value={e.role} onChange={ev=>updExp(i,'role',ev.target.value)} placeholder="Role/Title" className="border rounded px-2 py-1 text-xs col-span-1" />
                <input value={e.from||''} onChange={ev=>updExp(i,'from',ev.target.value)} type="date" className="border rounded px-2 py-1 text-xs" />
                <div className="flex gap-1">
                  <input value={e.to||''} onChange={ev=>updExp(i,'to',ev.target.value)} type="date" className="border rounded px-2 py-1 text-xs" />
                  <button onClick={()=>delExp(i)} className="text-rose-500 text-xs px-1">✕</button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="px-3 py-1.5 bg-[#3b4872] text-white text-xs rounded-sm" data-testid="career-save-btn">{saving ? 'Saving…' : 'Save'}</button>
            <button onClick={()=>setEditing(false)} className="px-3 py-1.5 border border-slate-300 text-xs rounded-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
